import { Alert, Button, Card, Flex, Heading, Link, Span, Text, Wrap } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, toaster } from '@shared';
import { useMobileContext } from '@shared';
import apiKeysService from '../../../../api/services/apiKeysService';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { IoCopyOutline } from 'react-icons/io5';
import { LuExternalLink } from 'react-icons/lu';
import { createColumnHelper } from '@tanstack/react-table';
import { useDrawer } from '@admin';
import { ApiKeyDrawer } from './ApiKeyDrawer';

const columnHelper = createColumnHelper<ApiKey>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Api() {
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const [key, setKey] = useState<ApiKey>();

	function handleCopy(): void {
		if (key?.apiKey) {
			navigator.clipboard.writeText(key.apiKey);
			toaster.create({
				type: 'success',
				title: t('general.copied_to_clipboard'),
			});
		}
	}

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<ApiKey>(), []);

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contactEmail', {
			header: t('general.contact_email'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeCreated) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	const { openDrawer } = useDrawer();

	return (
		<Wrap gap={4} width={'100%'}>
			<Card.Root maxWidth={'2000px'} width="70%" flex={1} minWidth={'340px'}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDir="column" justifyContent={'space-between'} padding={5} gap={5}>
					<Heading size="sm">{t('general.api_keys')}</Heading>
					<Flex display={'flex'} flexDirection={'column'} gap={2} flex={1} maxWidth={'100%'}>
						{key ? (
							<Alert.Root status="success" marginBottom={4}>
								<Alert.Indicator />
								<Alert.Content>
									<Alert.Title wordBreak={'break-word'}>{t('general.api_key_generated')}</Alert.Title>
									<Alert.Description wordBreak={'break-word'} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<Span>
											<IoCopyOutline size="20" cursor="pointer" type="button" onClick={handleCopy} />
										</Span>
										<code onClick={handleCopy} style={{ userSelect: 'all' }}>
											{key?.apiKey}
										</code>
									</Alert.Description>
								</Alert.Content>
							</Alert.Root>
						) : null}
						<DataCollectionContextProvider
							dataKey="apiKeys"
							keyAttribute="apiKeyID"
							emptyDataText={t('general.no_api_keys_found')}
							fetchFn={(...args) => apiKeysService.getAllResources(...args)}
							initialItemsPerPage={25}
							columns={columns}
							isServerSide={false}
							isSelectable={false}
							defaultSort={{ id: 'timeCreated', desc: true }}
							onClickRow={row => {
								openDrawer({
									id: 'apiKeyDrawer',
									content: <ApiKeyDrawer apiKeyID={row.original.apiKeyID} />,
								});
							}}
						>
							<Flex gap={2} flexWrap={'wrap'}>
								<DataCollectionTableHeader />
								<Button
									data-cy="addApiKeyButton"
									onClick={() =>
										openDrawer({
											id: 'apiKeyDrawer',
											content: <ApiKeyDrawer onSuccess={(apiKey: ApiKey) => setKey(apiKey)} />,
										})
									}
								>
									{t('general.create')}
								</Button>
							</Flex>
							<DataCollectionTable />
						</DataCollectionContextProvider>
					</Flex>
				</Card.Body>
			</Card.Root>
			<Card.Root height="fit-content" width={isMobile ? '100%' : '30%'} display={'flex'} flexDirection={'column'} gap={3}>
				<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
					<Flex display={'flex'} flexDirection={'column'} flex={1} maxWidth={'1200px'}>
						<Heading size="md" marginBottom={2}>
							{t('general.guide')}
						</Heading>
						<Text>{t('general.we_recommend_turning_on_automatic_payments')}</Text>
						<Text mt={2}>
							<Link textDecor={'underline'} color="rgb(61, 213, 152)" href="/credits?tab=automaticCreditPurchase" target="_blank">
								{t('general.automatic_credit_purchase')}
								<LuExternalLink />
							</Link>
						</Text>
						<Text mt={2}>
							<Link
								textDecor={'underline'}
								color="rgb(61, 213, 152)"
								href="https://tavoittaja.fi/api-docs/"
								target="_blank"
							>
								{t('general.api_documentation')}
								<LuExternalLink />
							</Link>
						</Text>
					</Flex>
				</Card.Body>
			</Card.Root>
		</Wrap>
	);
}
